import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import {
  ModalDialog,
  Radio,
  RadioGroup,
  SecondaryButton,
  TextButton,
  Toggle
} from '@smartbear/swagger-portal-component-library'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import Form from '~/shared/components/Form'
import { useNotification } from '~/shared/hooks/useNotification.ts'
import { RoutingOption } from '~/shared/types/portal'

export interface RoutingSettingsModalProps {
  isOpen?: boolean;
  initialRouting?: RoutingOption;
  initialCredentialsEnabled?: boolean;
  onConfirm?: (routing: RoutingOption, credentialsEnabled: boolean) => void;
  onClose?: () => void;
}

export const RoutingSettingsModal = ({
  isOpen = false,
  initialRouting = RoutingOption.BROWSER,
  initialCredentialsEnabled = true,
  onConfirm,
  onClose,
}: RoutingSettingsModalProps) => {
  const { t } = useTranslation()

  const focusedRadio = useRef<HTMLInputElement>(null)

  const { flashSuccess } = useNotification()

  const [routing, setRouting] = useState<RoutingOption>(initialRouting)
  const [credentialsEnabled, setCredentialsEnabled] = useState(initialCredentialsEnabled)

  const schema = useMemo(() => z.object({
    routing: z.string().default(initialRouting),
    credentialsEnabled: z.boolean().default(initialCredentialsEnabled),
  }), [initialRouting, initialCredentialsEnabled])

  const close = () => {
    onClose?.()
  }

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    flashSuccess({ message: t('portalStatusCodes.success.2008') })
    onConfirm?.(routing, credentialsEnabled)
    close()
  }

  useEffect(() => {
    focusedRadio.current?.focus()
  }, [])

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={close}
      className="sp-routing-modal"
      translations={{
        title: t('components.ui.apiViewer.routing.modal.title'),
        description: t('components.ui.apiViewer.routing.modal.subtitle'),
      }}
    >
      <Form schema={schema} mode="onChange" onSubmit={submit}>
        {({ Field, formState, register }) => (
          <section className="relative flex flex-col gap-4">
            <RadioGroup className="flex flex-col gap-4 text-body2">
              <Field name="routing" onChange={(event: ChangeEvent<HTMLInputElement>) => setRouting(event.target.value as RoutingOption)}>
                {() => <Radio
                  ref={routing === RoutingOption.BROWSER ? focusedRadio : null}
                  value={RoutingOption.BROWSER}
                  aria-label={t('components.ui.apiViewer.routing.modal.options.browser.label')}
                  id="browser"
                  label={t('components.ui.apiViewer.routing.modal.options.browser.label')}
                  containerClassName="sp-routing-modal-radio"
                  className="hover:border-(--sp-color-link) checked:border-(--sp-color-link) checked:hover:border-(--sp-color-link) dark:hover:border-(--sp-color-link) dark:checked:border-(--sp-color-link) dark:checked:hover:border-(--sp-color-link)"
                  innerElementClassName="bg-(--sp-color-link) peer-hover:bg-(--sp-color-link) dark:bg-(--sp-color-link) dark:peer-hover:bg-(--sp-color-link)"
                />}
              </Field>
              <Field name="credentialsEnabled" className="ml-5 -mt-1">
                {() => <Toggle
                  {...register('credentialsEnabled')}
                  id="credentialsEnabled"
                  name="credentialsEnabled"
                  data-testid="routing-cors-credentials-toggle"
                  onChange={(value: boolean) => setCredentialsEnabled(value)}
                  checked={credentialsEnabled}
                  infoTooltip={t('components.ui.apiViewer.routing.modal.options.browser.credentialsEnabled.tooltip')}
                  className="sp-routing-modal-toggle bg-(--sp-color-link) dark:bg-(--sp-color-link)"
                  labelClassName="text-(--sp-color-text-muted)"
                  tooltipTriggerInactiveClassName="text-(--sp-color-link) dark:text-(--sp-color-link)"
                  tooltipTriggerActiveClassName="text-(--sp-color-text-strong) dark:text-(--sp-color-text-strong)"
                >
                  {t('components.ui.apiViewer.routing.modal.options.browser.credentialsEnabled.label')}
                </Toggle>}
              </Field>
              <Field name="routing" onChange={(event: ChangeEvent<HTMLInputElement>) => setRouting(event.target.value as RoutingOption)}>
                {() => <Radio
                  ref={routing === RoutingOption.PROXY ? focusedRadio : null}
                  value={RoutingOption.PROXY}
                  aria-label={t('components.ui.apiViewer.routing.modal.options.proxy.label')}
                  id="proxy"
                  label={t('components.ui.apiViewer.routing.modal.options.proxy.label')}
                  containerClassName="sp-routing-modal-radio"
                  className="hover:border-(--sp-color-link) checked:border-(--sp-color-link) checked:hover:border-(--sp-color-link) dark:hover:border-(--sp-color-link) dark:checked:border-(--sp-color-link) dark:checked:hover:border-(--sp-color-link)"
                  innerElementClassName="bg-(--sp-color-link) peer-hover:bg-(--sp-color-link) dark:bg-(--sp-color-link) dark:peer-hover:bg-(--sp-color-link)"
                />}
              </Field>
            </RadioGroup>

            <div className="flex justify-end gap-2">
              <TextButton
                onClick={close}
                size="m"
                className="sp-routing-modal-cancel text-(--sp-color-text-muted)! dark:text-(--sp-color-text-muted)! hover:bg-(--sp-color-surface-inverse) dark:hover:bg-(--sp-color-surface-inverse) hover:text-(--sp-color-text-inverse)! dark:hover:text-(--sp-color-text-inverse)!"
              >
                {t('components.ui.buttons.cancel.label')}
              </TextButton>

              <SecondaryButton
                disabled={!formState.isValid}
                size="m"
                type="submit"
                className="sp-routing-modal-confirm border-(--sp-color-accent) dark:border-(--sp-color-accent) text-(--sp-routing-modal-confirm-text-color,var(--neutral-98)) dark:text-(--sp-routing-modal-confirm-text-color,var(--neutral-20)) hover:bg-(--sp-color-surface-inverse) dark:hover:bg-(--sp-color-surface-inverse) hover:border-(--sp-color-surface-inverse) dark:hover:border-(--sp-color-surface-inverse) hover:text-(--sp-color-text-inverse) dark:hover:text-(--sp-color-text-inverse)"
              >
                {t('components.ui.buttons.confirm.label')}
              </SecondaryButton>
            </div>
          </section>)}
      </Form>
    </ModalDialog>
  )
}